/**
 * Frontend presentation of frozen A6 Scan/Control temporal relation.
 * Mirrors classify_scan_control_temporal_relation. Does not infer Control T.
 */

export const RELATION_PRE = 'PRE';
export const RELATION_CROSSING = 'CROSSING';
export const RELATION_POST = 'POST';
export const RELATION_NOT_APPLICABLE = 'NOT_APPLICABLE';

export const CROSSING_CUSTOMER_COPY =
  'This Scan spans the time the Control was implemented. It is retained in the Case history but is not used as a clean before/after comparison.';

export const RELATION_BUYER_LABEL = {
  PRE: 'Baseline',
  CROSSING: 'Crosses the recorded Control date',
  POST: 'Follow-up',
} as const;

export const PRE_CUSTOMER_COPY = 'Before the recorded Control';
export const POST_CUSTOMER_COPY = 'After the recorded Control';

export type ScanControlRelationKind =
  | typeof RELATION_PRE
  | typeof RELATION_CROSSING
  | typeof RELATION_POST
  | typeof RELATION_NOT_APPLICABLE;

export type ScanControlRelationView = {
  relation: ScanControlRelationKind;
  label: string | null;
  customerCopy: string | null;
};

function asInstant(value?: string | null): number | null {
  const raw = String(value || '').trim();
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Classify one Scan window against one Control implemented_at.
 * Half-open [S, E): T == E is PRE; T == S is POST; S < T < E is CROSSING.
 */
export function presentScanControlRelation(args: {
  windowStart?: string | null;
  windowEnd?: string | null;
  controlRevisionId?: string | null;
  implementedAt?: string | null;
}): ScanControlRelationView {
  const revision = String(args.controlRevisionId || '').trim();
  const start = asInstant(args.windowStart);
  const end = asInstant(args.windowEnd);
  const t = asInstant(args.implementedAt);
  if (!revision || t == null) {
    return { relation: RELATION_NOT_APPLICABLE, label: null, customerCopy: null };
  }
  if (start == null || end == null || start >= end) {
    return { relation: RELATION_NOT_APPLICABLE, label: null, customerCopy: null };
  }
  if (end <= t) {
    return { relation: RELATION_PRE, label: RELATION_BUYER_LABEL.PRE, customerCopy: PRE_CUSTOMER_COPY };
  }
  if (start < t && t < end) {
    return {
      relation: RELATION_CROSSING,
      label: RELATION_BUYER_LABEL.CROSSING,
      customerCopy: CROSSING_CUSTOMER_COPY,
    };
  }
  return { relation: RELATION_POST, label: RELATION_BUYER_LABEL.POST, customerCopy: POST_CUSTOMER_COPY };
}

export function extractControlImplementation(opened?: {
  workflow_summary?: Record<string, any> | null;
  safety_case?: { recommendations?: any[] | null } | null;
  finding?: { recommendations?: any[] | null } | null;
} | null): { controlRevisionId: string | null; implementedAt: string | null } {
  const summary = opened?.workflow_summary || {};
  const actions: any[] = [
    ...((summary.corrective_actions?.active_actions as any[]) || []),
    ...((summary.corrective_actions?.completed_actions as any[]) || []),
    ...((summary.corrective_actions?.cancelled_actions as any[]) || []),
  ];
  const recs = opened?.safety_case?.recommendations || opened?.finding?.recommendations || [];
  recs.forEach((rec: any) => {
    if (rec?.linked_corrective_action) actions.push(rec.linked_corrective_action);
    if (Array.isArray(rec?.linked_corrective_actions)) actions.push(...rec.linked_corrective_actions);
  });
  const action = actions.find((item) => item && (item.implemented_at || item.control_revision_id));
  if (!action) return { controlRevisionId: null, implementedAt: null };
  return {
    controlRevisionId: String(action.control_revision_id || action.id || '').trim() || null,
    implementedAt: String(action.implemented_at || '').trim() || null,
  };
}
